import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Video, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentVideos = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!athlete) return;
      setLoading(true);
      try {
        const videosRes = await axios.get(`${API}/athletes/${athlete.id}/videos`, { headers: getAuthHeaders() });
        setVideos(videosRes.data);
      } catch (error) {
        console.error('Failed to fetch videos:', error);
        setVideos([]);
      }
      setLoading(false);
    };
    fetchData();
  }, [athlete, getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-videos">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Video Analysis</h1>
        <p className="text-muted-foreground">{athlete?.name}'s game footage and feedback</p>
      </div>

      {videos.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No videos uploaded yet</p>
          <p className="text-sm text-muted-foreground mt-2">Your coach will add game footage and analysis here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {videos.map((video) => (
            <div key={video.id} className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold mb-1">{video.title}</h3>
                  <p className="text-sm text-muted-foreground capitalize">{video.video_type.replace('_', ' ')}</p>
                </div>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <ExternalLink size={16} />
                  Watch
                </a>
              </div>
              {video.description && (
                <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed mt-3">{video.description}</p>
              )}
              {video.timestamp_notes && video.timestamp_notes.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-primary">Coach Notes:</p>
                  {video.timestamp_notes.map((note, idx) => (
                    <div key={idx} className="text-sm bg-muted/30 p-2 rounded-sm">
                      <span className="text-primary">[{note.time}]</span> {note.note}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Added: {new Date(video.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentVideos;
